import { useQuery } from "@tanstack/react-query";
import ConfigHolder from "../../config/ConfigHolder";
import useDataStore from "../../store/useDataStore";
import useWidgetStore from "../../store/useWidgetStore";
import { useSettings } from "../../context/SettingsContext";
export const useUpdateCart = (formContext, options) => {
    const { api_key, widget_id } = useSettings();
    const defaultOptions = Object.assign(Object.assign({}, options), { enabled: false, networkMode: "always", cacheTime: 0, staleTime: 0 });
    const { booking, cart, consumer, language, selectedResource, selectedCapacityGroups, setCart, getDaoAppointments, getDaoProducts } = useWidgetStore();
    const { apptypes, } = useDataStore();
    return useQuery(Object.assign(Object.assign({}, defaultOptions), { queryKey: ['widget-updateCart'], queryFn: async ({ signal }) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            let url = ConfigHolder.getApiMethodUrl(api_key, widget_id, 'updateCart', 'checkout');
            let _appointments = getDaoAppointments(), _paymentMethod = formContext.getValues('paymentMethod'), 
            // A subscription or membership can only ever pay for an appointment. Its own line is
            // in the cart, so getDaoProducts would send it straight back as another one to buy —
            // the backend then adds a second one alongside the one it creates itself, and the cart
            // grows by one on every refetch (toggling direct/incasso on the mandate form is enough
            // to do it). So that line is left out, but the products are not: a subscription never
            // pays for those, and posting none of them makes the backend rebuild the order without
            // them (it does that for every request that carries a subscriptionTypeId), which is how
            // buying a strippenkaart used to drop the products from the order and from the payment.
            _products = getDaoProducts().filter((product) => !["subscription", "membership"].includes(product.type)), selectedSubscription = formContext.getValues('selectedSubscription'), 
            // Only a resource the consumer actually picked (not the -1 "no preference"
            // sentinel) counts - otherwise the backend has nothing to distinguish a real
            // pick from an appointment that never showed a resource control at all, and
            // marks every resourceAssignment 'manual' instead of only the ones that are.
            hasSelectedResource = selectedResource
                ? selectedResource.filter((resourceId) => resourceId !== -1).length > 0
                : false;
            if (!selectedSubscription) {
                let _paymentMethod = formContext.getValues('paymentMethod');
                if (_paymentMethod === null || _paymentMethod === void 0 ? void 0 : _paymentMethod.startsWith('membership_')) {
                    let pm = _paymentMethod.substring(11);
                    if (pm.endsWith('mandate')) {
                        pm = pm.substring(0, pm.indexOf('_mandate'));
                    }
                    selectedSubscription = (_a = consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === parseInt(pm));
                }
                // Same fallback, for the same reason: without it, subscriptionTypeId below can
                // go missing from the request, which is what the backend's abort-the-previous-
                // order step (CheckoutController's _saveCart) gates on - a strippenkaart
                // submission that hits this would leave the previous order's appointment slots
                // held, so a following submission collides with them ("gekozen tijdstip is niet
                // meer beschikbaar").
                else if (_paymentMethod === null || _paymentMethod === void 0 ? void 0 : _paymentMethod.startsWith('subscription_')) {
                    let pm = _paymentMethod.substring(13);
                    if (pm.endsWith('addSubscription')) {
                        pm = pm.substring(0, pm.indexOf('_addSubscription'));
                    }
                    else if (pm.endsWith('addPayment')) {
                        pm = pm.substring(0, pm.indexOf('_addPayment'));
                    }
                    selectedSubscription = (_b = consumer.subscriptions) === null || _b === void 0 ? void 0 : _b.find((subscription) => subscription.id === parseInt(pm));
                }
            }
            let data = {
                order_id: (_c = cart.order_id) !== null && _c !== void 0 ? _c : '',
                giftCards: cart.giftCards,
                discounts: cart.discounts,
                // each appointment's own appTypeId/resourceId/capacity, not the globally
                // selected* state - that only reflects whichever appointment is currently
                // being edited on the select view, and would collapse every other
                // appointment in the cart onto it
                appointments: _appointments.length > 0 ? _appointments.map((_appointment) => {
                    var _a, _b, _c, _d, _e;
                    return {
                        appTypeId: _appointment.appTypeId,
                        resourceId: hasSelectedResource ? _appointment.resourceId : null,
                        // Default 1, not null - the backend's price lookup for a
                        // capacity-priced apptype needs a real capacity even when no
                        // capacity control was shown
                        capacity: (_a = _appointment.capacity) !== null && _a !== void 0 ? _a : 1,
                        components: [_appointment],
                        date: (_b = _appointment.date) !== null && _b !== void 0 ? _b : _appointment.time,
                        time: _appointment.time,
                        fields: (_c = consumer.fields) !== null && _c !== void 0 ? _c : {},
                        remark: (_d = booking.remark) !== null && _d !== void 0 ? _d : ((_e = consumer.remark) !== null && _e !== void 0 ? _e : '')
                    };
                }) : [],
                products: _products,
                paymentMethod: formContext.getValues('paymentMethod'),
                paymentOption: formContext.getValues('paymentOption'),
                final: formContext.getValues('final') && formContext.getValues('paymentMethod') !== "alternate",
                widgetUrl: window.location.href,
                subscriptionId: (_d = selectedSubscription === null || selectedSubscription === void 0 ? void 0 : selectedSubscription.subscriptionId) !== null && _d !== void 0 ? _d : undefined,
                subscriptionTypeId: (_e = selectedSubscription === null || selectedSubscription === void 0 ? void 0 : selectedSubscription.id) !== null && _e !== void 0 ? _e : undefined
            };
            if ((_f = cart.order_id) === null || _f === void 0 ? void 0 : _f.startsWith('tmp_')) {
                let _consumer = {
                    hash: (_g = consumer.hash) !== null && _g !== void 0 ? _g : '',
                    id: (_h = consumer.id) !== null && _h !== void 0 ? _h : ''
                };
                data.customer = _consumer;
            }
            if (formContext.getValues("createMandate")) {
                data.createMandate = true;
            }
            const response = await fetch(url.href, {
                signal: signal,
                method: "POST",
                headers: {
                    'Content-Type': 'application/json', // checks this header if it's a POST, while calling the method getRestData.... That's not Restful
                    'X-OAnl-csrf-protection': 'on',
                    'accept': 'application/json+ld',
                },
                body: JSON.stringify(data)
            });
            let result = response.json();
            setCart(Object.assign(Object.assign({}, cart), { secureToken: (_j = response.headers.get('x-oanl-secure-token')) !== null && _j !== void 0 ? _j : '' }));
            return result;
        } }));
};
